<template>
    <ds-modal
        is-open
        show-close
        class="create-custom-field-modal"
        :title="title"
        @close="onCloseModal"
    >
        <template #header>
            <ds-icon-button
                name="help-circle"
                data-qa="help-circle"
                @click.stop="helpLink"
            />
        </template>
        <div v-if="loading" class="spinner-container" data-qa="loading-spinner">
            <ds-spinner />
        </div>
        <div v-else class="add-custom-field-inputs">
            <p>{{ customFieldAvailableText }}</p>
            <p v-if="isApproachingCustomFieldLimit" class="max-field-description-warning">
                {{ maxCountWarningDisplayText }}
            </p>
            <p v-if="isApproachingCustomFieldLimit">
                {{ maxCountReachedWarningText }}
            </p>
            <ds-input-field
                ref="fieldName"
                v-model="customField.name"
                type="text"
                required
                autofocus
                :label="fieldNameLabel"
                :maxlength="maxFieldLength"
                :submitted="submitted || isNameDuplicate"
                :invalid="!submitted && isNameDuplicate"
                data-qa="field-name"
            >
                <template #help>
                    <small class="name-length">
                        {{ customField.name.length }}/{{ maxFieldLength }}
                    </small>
                </template>

                <template #error>
                    <small v-if="isNameDuplicate" data-qa="duplicate-error">
                        {{ duplicateFieldNameError }}
                    </small>
                </template>
            </ds-input-field>
            <ds-multiselect
                v-model="customField.fieldType"
                label-prop="name"
                value-prop="id"
                bind-value-only
                required
                searchable
                show-search-icon
                :placeholder="fieldTypeLabel"
                :options="fieldTypes"
                :submitted="submitted"
                data-qa="field-type"
                class="field-type-dropdown"
            />
            <div class="help" @click="helpLink">
                {{ helpText }}
            </div>
        </div>
        <section v-if="showOptionsField">
            <hr />
            <h6 class="semibold">
                {{ optionsFieldLabel }}
            </h6>

            <div class="field-options">
                <h6 class="semibold">
                    <ds-text-area-field
                        v-model="customFieldOptions"
                        data-qa="custom-field-values"
                        required
                        :label="fieldOptionsFieldLabel"
                        :submitted="submitted"
                    />

                    <small>{{ optionsHelpText }}</small>

                    <div v-if="showSettingsField" class="field-settings">
                        <p />
                        <ds-checkbox
                            v-model="isDefaultToFirstOptionChecked"
                            :label="defaultFirstOptionLabel"
                        />

                        <small>{{ defaultFirstOptionHelpText }}</small>
                    </div>
                </h6>
            </div>
        </section>
        <hr />
        <div class="button-row">
            <ds-filled-button
                class="create-field-button"
                :loading="loading"
                :disabled="disabled"
                @click="onCreateCustomField"
            >
                {{ createFieldButtonLabel }}
            </ds-filled-button>
            <ds-text-button
                :disabled="loading"
                data-qa="cancel-button"
                @click="onCloseModal"
            >
                {{ cancelButtonLabel }}
            </ds-text-button>
        </div>
    </ds-modal>
</template>

<script>
import { customFieldTypeLabels } from '../util/marketing/site.utils';
import contactFieldTypes from '@/components/contact/contactFieldTypes';

export default {
    props: {
        customFields: Array,
        maxCustomFields: {
            type: Number, // maxmimum number of custom contact fields
            default: 150,
        },
        maxFieldLength: {
            type: Number,
            default: 64,
        },
    },

    data() {
        return {
            loading: false,
            submitted: false,
            isDirty: false,
            customField: {
                name: '',
                fieldType: '',
                recordType: 'contact',
                alwaysShown: true,
            },
            isDefaultToFirstOptionChecked: false,
            customFieldOptions: null,
            fieldTypes: Object.keys(contactFieldTypes.CUSTOM_FIELD_TYPES).map((key) => {
                const id = contactFieldTypes.CUSTOM_FIELD_TYPES[key];
                const name = customFieldTypeLabels[id];

                return ({ id, name });
            }).filter(({ id, name }) => !!id && !!name && id !== 'User'),
        };
    },

    computed: {
        title() {
            if (this.customFields.length === this.maxCustomFields) {
                return `You can only have a maximum of ${this.maxCustomFields} custom fields in contact records. Before creating any new fields, delete existing fields that are not being used to store data.`;
            }

            return 'Create custom contact field';
        },

        customFieldsAvailable() {
            return this.maxCustomFields - this.customFields.length;
        },

        showOptionsField() {
            return !!(this.customField.fieldType && contactFieldTypes.CUSTOM[this.customField.fieldType].hasOptions);
        },

        showSettingsField() {
            return !!(this.customField.fieldType && contactFieldTypes.CUSTOM[this.customField.fieldType].hasSettings);
        },

        isApproachingCustomFieldLimit() {
            return this.customFieldsAvailable <= this.maxCustomFields * 0.10;
        },

        fieldNameLabel() {
            return 'Field name';
        },

        fieldTypeLabel() {
            return 'Field type';
        },

        helpText() {
            return 'Which type of field should I use?';
        },

        duplicateFieldNameError() {
            return 'This field name is already being used';
        },

        customFieldAvailableText() {
            return `You can create up to ${this.customFieldsAvailable} custom contact fields. This is a global action. Any fields you add here will be accessible from all of your contacts`;
        },

        maxCountWarningDisplayText() {
            return `${this.customFields.length}/${this.maxCustomFields} custom fields used`;
        },

        maxCountReachedWarningText() {
            return 'Are there any customFields you could delete? If you do, they’ll be removed from every location where they’re being used.';
        },

        createFieldButtonLabel() {
            return 'Create field';
        },

        optionsFieldLabel() {
            return 'Build your field';
        },

        optionsHelpText() {
            return 'Enter one option per line. Field options will be displayed as a list in the order you create them.';
        },

        fieldOptionsFieldLabel() {
            return 'Field Options';
        },

        defaultFirstOptionLabel() {
            return 'Display the first listed option as the default selection.';
        },

        defaultFirstOptionHelpText() {
            return '(If unchecked, the default selection will display the field title, such as “office location.“)';
        },

        cancelButtonLabel() {
            return 'Cancel';
        },

        disabled() {
            return !this.customField.name || !this.customField.fieldType || this.isNameDuplicate;
        },

        isNameDuplicate() {
            return this.customFields.map((customField) => customField.name).includes(this.customField.name);
        },
    },

    methods: {
        handleContentUpdated(isDirty) {
            this.isDirty = isDirty;
        },

        onCreateCustomField() {
            const newCustomField = { ...this.customField };

            if (this.showOptionsField) {
                newCustomField.options = (this.customFieldOptions || '').replaceAll(/,/g, '\n').trim().split('\n').filter(
                    (value) => value.length > 0,
                );
            }

            if (this.showSettingsField) {
                newCustomField.defaultToFirstOption = this.isDefaultToFirstOptionChecked;
            }

            this.$emit('new-field-added', newCustomField);
        },

        onCloseModal() {
            this.$emit('close');
        },

        helpLink() {
            if (this.customField.length === this.maxCustomFields) {
                window.open('https://help.keap.com/help/custom-fields-management#delete-a-custom-contact-field', '_blank');
            } else {
                window.open('https://new-infusionsoft.knowledgeowl.com/help/custom-field-types', '_blank');
            }
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" type="text/scss">
.multiselect.field-type-dropdown {
    &.searchable {
        margin-bottom: 0;
    }
    input {
        height: auto;
    }
}
</style>

<style lang="scss" rel="stylesheet/scss" type="text/scss" scoped>
.create-custom-field-modal {
    padding: 1.25rem;
}

.button-row {
    display: grid;
    grid-template-columns: auto auto;
    margin-right: auto;
}

.help {
    color: #0042db;
    font-size: .75rem;
    cursor: pointer;
    margin: 0.5rem;
}

.always-shown .caption {
    color: rgba(0,0,0,.6);
}

.max-field-description-warning {
    background-color: #ffe15a;
}

h6.semibold {
    text-transform: unset;
    padding: 0.5rem 0;
}

</style>
